import { MAX_PHOTO_BYTES } from '@bifeai/shared';

export interface PreparedPhoto {
  blob: Blob;
  contentType: 'image/webp' | 'image/jpeg';
  previewUrl: string;
}

const MAX_SIDE = 1000;

/**
 * Achica la foto en el navegador antes de subirla: lado mayor de 1000 px, WebP (o JPEG si el
 * navegador no sabe generar WebP). Una foto de celular de 5 MB queda en unos 80–150 KB,
 * carga rápido en el mostrador y no gasta almacenamiento.
 */
export async function preparePhoto(file: File): Promise<PreparedPhoto> {
  if (!file.type.startsWith('image/')) throw new Error('El archivo no es una imagen.');
  if (file.size > 25 * 1024 * 1024) throw new Error('La imagen es demasiado grande.');

  const bitmap = await loadBitmap(file);
  const scale = Math.min(1, MAX_SIDE / Math.max(bitmap.width, bitmap.height));
  const width = Math.max(1, Math.round(bitmap.width * scale));
  const height = Math.max(1, Math.round(bitmap.height * scale));

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Tu navegador no permite procesar imágenes.');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(bitmap.source, 0, 0, width, height);
  bitmap.close();

  let blob = await toBlob(canvas, 'image/webp', 0.82);
  let contentType: PreparedPhoto['contentType'] = 'image/webp';
  if (!blob || blob.type !== 'image/webp') {
    blob = await toBlob(canvas, 'image/jpeg', 0.85);
    contentType = 'image/jpeg';
  }
  if (!blob) throw new Error('No se pudo procesar la imagen.');
  if (blob.size > MAX_PHOTO_BYTES) throw new Error('La foto sigue pesando más de 2 MB. Probá con otra.');
  return { blob, contentType, previewUrl: URL.createObjectURL(blob) };
}

async function loadBitmap(file: File): Promise<{
  source: CanvasImageSource;
  width: number;
  height: number;
  close: () => void;
}> {
  if ('createImageBitmap' in window) {
    try {
      // Respeta la orientación EXIF de las fotos sacadas con el celular.
      const bmp = await createImageBitmap(file, {
        imageOrientation: 'from-image',
      });
      return {
        source: bmp,
        width: bmp.width,
        height: bmp.height,
        close: () => bmp.close(),
      };
    } catch {
      /* se intenta con <img> */
    }
  }
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.decoding = 'async';
    img.src = url;
    await img.decode();
    return {
      source: img,
      width: img.naturalWidth,
      height: img.naturalHeight,
      close: () => undefined,
    };
  } catch {
    throw new Error('No se pudo leer la imagen. Probá con una foto JPG o PNG.');
  } finally {
    URL.revokeObjectURL(url);
  }
}

function toBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, type, quality));
}
